import type { EnvironmentId, ScopedThreadRef, ThreadId } from "@cmmd-forge/contracts";
import { useCallback } from "react";
import {
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  type ComposerTrigger,
} from "../../composer-logic";
import type { DraftId } from "../../draftId";
import { shouldNavigateComposerHistory } from "./ChatComposer.logic";
import { usePromptHistoryRecall } from "./usePromptHistoryRecall";

/** Keeps prompt history and provider trait insertion on the same cursor update path. */
export function useComposerPromptNavigation(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly promptRef: { current: string };
  readonly composerDraftTarget: ScopedThreadRef | DraftId;
  readonly setComposerDraftPrompt: (target: ScopedThreadRef | DraftId, prompt: string) => void;
  readonly setComposerCursorIfChanged: (cursor: number) => void;
  readonly setComposerTriggerIfChanged: (trigger: ComposerTrigger | null) => void;
  readonly scheduleComposerFocus: () => void;
  readonly composerDraft: {
    readonly images: readonly unknown[];
    readonly files?: readonly unknown[];
    readonly terminalContexts: readonly unknown[];
  };
  readonly historyDisabled: boolean;
}): {
  readonly setPromptFromTraits: (nextPrompt: string) => void;
  readonly navigatePromptHistory: (
    key: "ArrowUp" | "ArrowDown",
    event: KeyboardEvent,
    menuIsActive: boolean,
    editor: { isCaretOnVisualEdge: (edge: "start" | "end") => boolean } | null,
    editorValue: string,
  ) => boolean;
} {
  const {
    environmentId,
    activeThreadId,
    promptRef,
    composerDraftTarget,
    setComposerDraftPrompt,
    setComposerCursorIfChanged,
    setComposerTriggerIfChanged,
    scheduleComposerFocus,
    composerDraft,
    historyDisabled,
  } = input;
  const applyPromptAtEnd = useCallback(
    (nextPrompt: string) => {
      promptRef.current = nextPrompt;
      setComposerCursorIfChanged(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
      setComposerTriggerIfChanged(detectComposerTrigger(nextPrompt, nextPrompt.length));
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    },
    [
      composerDraftTarget,
      promptRef,
      setComposerCursorIfChanged,
      setComposerDraftPrompt,
      setComposerTriggerIfChanged,
    ],
  );
  const setPromptFromTraits = useCallback(
    (nextPrompt: string) => {
      if (nextPrompt !== promptRef.current) applyPromptAtEnd(nextPrompt);
      scheduleComposerFocus();
    },
    [applyPromptAtEnd, promptRef, scheduleComposerFocus],
  );
  const { navigate, isActive } = usePromptHistoryRecall({
    environmentId,
    activeThreadId,
    promptRef,
    hasAttachments:
      composerDraft.images.length > 0 ||
      Boolean(composerDraft.files?.length) ||
      composerDraft.terminalContexts.length > 0,
    disabled: historyDisabled,
    applyPrompt: applyPromptAtEnd,
  });
  /** Routes an arrow through recall only when the caret is on its visual edge. */
  const navigatePromptHistory = (
    key: "ArrowUp" | "ArrowDown",
    event: KeyboardEvent,
    menuIsActive: boolean,
    editor: { isCaretOnVisualEdge: (edge: "start" | "end") => boolean } | null,
    editorValue: string,
  ): boolean => {
    const direction = key === "ArrowUp" ? "backward" : "forward";
    const edge = key === "ArrowUp" ? "start" : "end";
    return (
      shouldNavigateComposerHistory(
        menuIsActive,
        isActive(),
        editor?.isCaretOnVisualEdge(edge) ?? editorValue.length === 0,
      ) && navigate(direction, event)
    );
  };
  return { setPromptFromTraits, navigatePromptHistory };
}
